import { Chip, Grid, Stack, Typography } from '@mui/joy';
import { PencilSimple, Warning } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { Button, InfoCard, SlidingPanelContent } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Tooltip } from 'core/elements';
import { IInfoCardLine } from 'core/types';
import { spaSendBlockTooltipKeyMap, tradeAgreementStatusColorMap } from 'quotes/constants';
import { SpaSendBlockReason, SpaStatus, TradeAgreementStatus } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IAttributes } from 'quotes/types';
import {
  Color,
  DecoratorPosition,
  Direction,
  Level,
  Size,
  TooltipPosition,
  Variant,
} from 'theme/enums';

import {
  attributesColumnGridSizing,
  renderCustomersValue,
  renderLocationsValue,
  renderProjectValue,
  renderTradeAgreementOwnerValue,
  renderTradeAgreementStartDateValue,
} from './utils';

interface IProps {
  attributes: IAttributes | null;
  tradeAgreementStatus?: TradeAgreementStatus;
  tradeAgreementError?: string | null;
  tradeAgreementSentVersion?: number | null;
  // Why the stored audience is beyond this user's rights, null when it is not.
  spaAudienceBlockReason?: SpaSendBlockReason | null;
  sentSpaStatus?: SpaStatus | null;
  isEditBlocked?: boolean;
  onEdit: () => void;
}

export const AttributesCard = ({
  attributes,
  tradeAgreementStatus,
  tradeAgreementError,
  tradeAgreementSentVersion,
  spaAudienceBlockReason = null,
  sentSpaStatus,
  isEditBlocked = false,
  onEdit,
}: IProps) => {
  const { t } = useTranslation();

  const placeholder = DEFAULT_PLACEHOLDER;

  // A user with the rights may have saved it; without them, this user can't keep or send it.
  const notAllowedMessage = spaAudienceBlockReason
    ? t(spaSendBlockTooltipKeyMap[spaAudienceBlockReason])
    : '';
  const isProjectNotAllowed = SpaSendBlockReason.PROGRAM_NOT_PERMITTED === spaAudienceBlockReason;
  const isSalesLocationsNotAllowed =
    SpaSendBlockReason.ALL_CUSTOMERS_ALL_SALES_LOCATIONS_NOT_PERMITTED === spaAudienceBlockReason;

  // Same mark as LineView's required state; explicit size, since the value wrapper scales 1em
  // icons to 26px.
  const notAllowedIcon = (
    <Tooltip title={notAllowedMessage} color={Color.DANGER}>
      <Typography
        color={Color.DANGER}
        sx={{ display: 'flex', alignItems: 'center' }}
        aria-label={notAllowedMessage}
      >
        <Warning size={20} />
      </Typography>
    </Tooltip>
  );

  const withNotAllowedWarning = (
    value: IInfoCardLine['value'],
    isNotAllowed = !!spaAudienceBlockReason,
    iconPosition: DecoratorPosition = DecoratorPosition.END
  ): IInfoCardLine['value'] =>
    isNotAllowed ? (
      <Stack
        direction={Direction.ROW}
        sx={{ alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
      >
        {DecoratorPosition.START === iconPosition && notAllowedIcon}
        {value}
        {DecoratorPosition.END === iconPosition && notAllowedIcon}
      </Stack>
    ) : (
      value
    );

  // Read-only send-lifecycle status (Pending|Sent|Failed|Updated Post Sent) as a colored chip; the
  // value is already display text. Undefined when unset so InfoCard renders its default dash.
  const tradeAgreementStatusChip = tradeAgreementStatus ? (
    <Chip
      color={tradeAgreementStatusColorMap[tradeAgreementStatus] ?? Color.NEUTRAL}
      variant={Variant.SOLID}
      size={Size.SM}
    >
      {tradeAgreementStatus}
    </Chip>
  ) : undefined;

  // A Failed send carries a persisted error (surfaced verbatim); Updated Post Sent needs the
  // explanation its label can't carry. Every other status hovers to nothing.
  const updatedPostSentTooltip =
    VendorQuoteHelper.getUpdatedPostSentTooltip(tradeAgreementSentVersion);
  const tradeAgreementStatusTooltip =
    TradeAgreementStatus.FAILED === tradeAgreementStatus && tradeAgreementError
      ? { title: tradeAgreementError, color: Color.DANGER }
      : TradeAgreementStatus.UPDATED_POST_SENT === tradeAgreementStatus
      ? {
          title: t(updatedPostSentTooltip.key, updatedPostSentTooltip.options),
          color: Color.WARNING,
        }
      : null;

  const tradeAgreementStatusChipWithTooltip =
    tradeAgreementStatusChip && tradeAgreementStatusTooltip ? (
      <Tooltip {...tradeAgreementStatusTooltip} placement={TooltipPosition.TOP}>
        {tradeAgreementStatusChip}
      </Tooltip>
    ) : (
      tradeAgreementStatusChip
    );

  const sentSpaStatusLabelKey = VendorQuoteHelper.getSpaStatusLabelKey(sentSpaStatus);
  const hasSentVersion = typeof tradeAgreementSentVersion === 'number';

  const tradeAgreementStatusValue =
    tradeAgreementStatusChipWithTooltip && (hasSentVersion || sentSpaStatusLabelKey) ? (
      <Stack direction={Direction.COLUMN} sx={{ alignItems: 'flex-end', gap: 0.5 }}>
        {tradeAgreementStatusChipWithTooltip}
        <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
          {hasSentVersion && sentSpaStatusLabelKey
            ? t('labelSentAsSpaVersionStatus', {
                version: tradeAgreementSentVersion,
                status: t(sentSpaStatusLabelKey),
              })
            : hasSentVersion
            ? t('labelTradeAgreementSentVersion', { version: tradeAgreementSentVersion })
            : t('labelSentAsSpaStatus', { status: t(sentSpaStatusLabelKey as string) })}
        </Typography>
      </Stack>
    ) : (
      tradeAgreementStatusChipWithTooltip
    );

  const spaStatusLabelKey = VendorQuoteHelper.getSpaStatusLabelKey(attributes?.spaStatus);

  const baseInformationItems: IInfoCardLine[] = [
    {
      label: t('labelTradeAgreementStatus'),
      value: tradeAgreementStatusValue,
    },
    {
      label: t('labelTradeAgreementOwner'),
      value: renderTradeAgreementOwnerValue(attributes?.tradeAgreementOwner ?? null, placeholder),
    },
    {
      label: t('labelProject'),
      value: withNotAllowedWarning(
        renderProjectValue(
          attributes?.project ?? null,
          t('labelProjectNumber'),
          t('labelProgramNumber'),
          placeholder
        ),
        isProjectNotAllowed,
        DecoratorPosition.START
      ),
    },
    {
      label: t('labelSpaPriority'),
      value: attributes?.priorityTier
        ? t('labelSpaPriorityValue', { tier: attributes.priorityTier })
        : undefined,
    },
    {
      label: t('labelSpaStatus'),
      value: spaStatusLabelKey ? t(spaStatusLabelKey) : undefined,
    },
    {
      label: t('labelTradeAgreementStartDate'),
      value: renderTradeAgreementStartDateValue(attributes?.tradeAgreementStartDate),
    },
    {
      label: t('labelEnabledForSelectedSpa'),
      value: attributes?.selectableSpaEnabled ? t('labelYes') : t('labelNo'),
    },
  ];

  const customersItems: IInfoCardLine[] = [
    {
      label: t('labelCustomers'),
      value: attributes
        ? withNotAllowedWarning(
            renderCustomersValue(
              attributes,
              t('labelAllCustomers'),
              t('labelAccountNumberLong'),
              t('labelCustomers'),
              placeholder
            )
          )
        : undefined,
    },
  ];

  const locationsItems: IInfoCardLine[] = [
    {
      label: t('labelSalesLocations'),
      value: attributes
        ? withNotAllowedWarning(
            renderLocationsValue(
              attributes.salesLocations,
              t('labelLocationId'),
              t('labelSalesLocations'),
              placeholder,
              attributes.allSalesLocations,
              t('labelAllLocations')
            ),
            isSalesLocationsNotAllowed
          )
        : undefined,
    },
    {
      label: t('labelShippingLocations'),
      value: attributes
        ? renderLocationsValue(
            attributes.shippingLocations,
            t('labelLocationId'),
            t('labelShippingLocations'),
            placeholder
          )
        : undefined,
    },
  ];

  return (
    <SlidingPanelContent
      title={t('titleAttributes')}
      // fullWidth defaults to a fixed 1440px (sized for Part Details) that overflows this
      // narrower panel; false makes the wrapper flex to fit.
      fullWidth={false}
      sx={{ minWidth: 0 }}
      extra={
        <Button
          startDecorator={<PencilSimple />}
          text={t('buttonLabelEdit')}
          variant={Variant.SOFT}
          disabled={isEditBlocked}
          onClick={onEdit}
          tooltipProps={
            isEditBlocked
              ? {
                  placement: TooltipPosition.TOP,
                  color: Color.NEUTRAL,
                  size: Size.SM,
                  title: t('tooltipAttributesEditBlocked'),
                  children: null,
                }
              : undefined
          }
          // MUI swallows hover on disabled buttons; re-enable pointer events so the tooltip shows.
          sx={
            isEditBlocked
              ? {
                  pointerEvents: 'auto !important',
                  cursor: 'not-allowed !important',
                  '&:hover': { backgroundColor: 'transparent !important' },
                }
              : undefined
          }
        />
      }
    >
      <Grid container spacing={3} sx={{ minWidth: 0 }}>
        <Grid {...attributesColumnGridSizing} sx={{ minWidth: 0 }}>
          <InfoCard title={t('titleMainInfo')} items={baseInformationItems} />
        </Grid>

        <Grid {...attributesColumnGridSizing} sx={{ minWidth: 0 }}>
          <InfoCard title={t('titleCustomers')} items={customersItems} />
        </Grid>

        <Grid {...attributesColumnGridSizing} sx={{ minWidth: 0 }}>
          <InfoCard title={t('titleLocations')} items={locationsItems} truncateLabel={false} />
        </Grid>
      </Grid>
    </SlidingPanelContent>
  );
};
